import { useRef, useState } from 'react';
import useAuth from 'src/hooks/useAuth';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
// import { useNavigate } from 'react-router-dom';


import {
  Avatar,
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Popover,
  Tooltip,
  Typography,
  styled
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import InboxTwoToneIcon from '@mui/icons-material/InboxTwoTone';
import AccountBoxTwoToneIcon from '@mui/icons-material/AccountBoxTwoTone';
import LockOpenTwoToneIcon from '@mui/icons-material/LockOpenTwoTone';
import AccountTreeTwoToneIcon from '@mui/icons-material/AccountTreeTwoTone';

const UserBoxButton = styled(IconButton)(
  ({ theme }) => `
        width: 40px;
        height: 40px;
        padding: 3px;
        margin-left: ${theme.spacing(0.25)};

        .MuiAvatar-root {
          width: 34px;
          height: 34px;
          border-radius: 50%;
          font-size: ${theme.typography.pxToRem(13)};
        }
`
);

const MenuUserBox = styled(Box)(
  ({ theme }) => `
        background: ${theme.colors.alpha.black[5]};
        padding: ${theme.spacing(2)};
`
);

const UserBoxText = styled(Box)(
  ({ theme }) => `
        text-align: left;
        padding-left: ${theme.spacing(1)};
`
);

const UserBoxLabel = styled(Typography)(
  ({ theme }) => `
        font-weight: ${theme.typography.fontWeightBold};
        color: ${theme.palette.secondary.main};
        display: block;
`
);

// const UserBoxDescription = styled(Typography)(
//   ({ theme }) => `
//         color: ${theme.palette.secondary.light}
// `
// );

function HeaderUserbox() {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, setActiveShop } = useAuth();

  const ref = useRef(null);
  const [isOpen, setOpen] = useState(false);
  const [switchingShopId, setSwitchingShopId] = useState(null);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleLogout = async () => {
    try {
      handleClose();
      await logout();
      navigate('/');
    } catch (err) {
      console.error(err);
    }
  };

  const handleShopSwitch = async (shopId) => {
    if (Number(shopId) === Number(user?.shop?.id)) return;
    setSwitchingShopId(Number(shopId));
    try {
      await setActiveShop(shopId);
      handleClose();
      window.location.reload();
    } catch (error) {
      console.error('[account] shop switch failed', error);
      setSwitchingShopId(null);
    }
  };

  const initials = String(user.name || 'Account')
    .split(/\s+|@/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();

  return (
    <>
      <Tooltip arrow title="Account">
        <UserBoxButton color="secondary" ref={ref} onClick={handleOpen} aria-label="Open account menu">
          <Avatar alt={user.name} src={user.avatar}>{initials}</Avatar>
        </UserBoxButton>
      </Tooltip>
      <Popover
        disableScrollLock
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <MenuUserBox
          sx={{
            minWidth: 210
          }}
          display="flex"
        >
          <Avatar variant="rounded" alt={user.name} src={user.avatar}>{initials}</Avatar>
          <UserBoxText>
            <UserBoxLabel variant="body1">{user.name}</UserBoxLabel>
            {/* <UserBoxDescription variant="body2">
              {user.jobtitle}
            </UserBoxDescription> */}
          </UserBoxText>
        </MenuUserBox>
        <Divider
          sx={{
            mb: 0
          }}
        />
        {Array.isArray(user?.shops) && user.shops.length > 1 ? (
          <>
            <Box sx={{ px: 2, pt: 1.25 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                ACTIVE SHOP
              </Typography>
            </Box>
            <List dense sx={{ px: 1, pb: 1 }}>
              {user.shops.map((shop) => (
                <ListItem
                  key={shop.id}
                  button
                  selected={Number(shop.id) === Number(user?.shop?.id)}
                  disabled={Boolean(switchingShopId)}
                  onClick={() => handleShopSwitch(shop.id)}
                >
                  <ListItemText
                    primary={shop.name || `Shop ${shop.id}`}
                    secondary={Number(shop.id) === Number(user?.shop?.id) ? shop.role || 'active' : shop.role}
                  />
                </ListItem>
              ))}
            </List>
            <Divider />
          </>
        ) : null}
        <List
          sx={{
            p: 1
          }}
          component="nav"
        >
          <ListItem
            onClick={() => {
              handleClose();
            }}
            button
            to={`/${location.pathname.split('/')[1]}/management/services`}
            component={NavLink}
          >
            <AccountBoxTwoToneIcon fontSize="small" />
            <ListItemText primary={t('Manage Canned Jobs')} />
          </ListItem>
          <ListItem
            onClick={() => {
              handleClose();
            }}
            button
            to={`/${location.pathname.split('/')[1]}/management/parts-inventory`}
            component={NavLink}
          >
            <AccountBoxTwoToneIcon fontSize="small" />
            <ListItemText primary={t('Manage Parts Inventory')} />
          </ListItem>
          <ListItem
            onClick={() => {
              handleClose();
            }}
            button
            to={`/${
              location.pathname.split('/')[1]
            }/management/advisors`}
            component={NavLink}
          >
            <InboxTwoToneIcon fontSize="small" />
            <ListItemText primary={t('Add Service Advisors')} />
          </ListItem>
          <ListItem
            onClick={() => {
              handleClose();
            }}
            button
            to={`/${
              location.pathname.split('/')[1]
            }/management/technicians`}
            component={NavLink}
          >
            <AccountTreeTwoToneIcon fontSize="small" />
            <ListItemText primary={t('Add Technicians')} />
          </ListItem>
          <ListItem
            onClick={() => {
              handleClose();
            }}
            button
            to={`/${
              location.pathname.split('/')[1]
            }/management/payroll`}
            component={NavLink}
          >
            <AccountTreeTwoToneIcon fontSize="small" />
            <ListItemText primary={t('Payroll')} />
          </ListItem>
          <ListItem
            onClick={() => {
              handleClose();
            }}
            button
            to={`/${
              location.pathname.split('/')[1]
            }/management/settings`}
            component={NavLink}
          >
            <AccountTreeTwoToneIcon fontSize="small" />
            <ListItemText primary={t('Settings')} />
          </ListItem>
        </List>
        <Divider />
        <Box m={1}>
          <Button color="primary" fullWidth onClick={handleLogout}>
            <LockOpenTwoToneIcon
              sx={{
                mr: 1
              }}
            />
            {t('Sign out')}
          </Button>
        </Box>
      </Popover>
    </>
  );
}

export default HeaderUserbox;
